import { useEffect, useState } from 'react';
import axios from 'axios';
import { CreditCard, Check } from 'lucide-react';
import { API } from '@/App';

const STAGE_PCTS = { 1: 10, 2: 25, 3: 50, 4: 80, 5: 100 };
const inr = (n) => `₹${Math.round(n || 0).toLocaleString('en-IN')}`;

// Fee due = cumulative stage % × consultancy fee; stages 4-5 = higher of that vs site-work % of fee on executed work.
export function stageBilling(schedule, stage, { fee, basicTotal, siteBasic }) {
  const milestonePct = STAGE_PCTS[stage] || 0;
  const sched = (schedule || []).find((r) => Number(r.stage) === Number(stage)) || {};
  const siteWorkPct = Number(sched.pct_of_site_work) || 0;
  const higherPct = stage >= 4 ? Math.max(milestonePct, siteWorkPct) : milestonePct;
  const feeRate = basicTotal > 0 ? fee / basicTotal : 0;
  const milestoneFee = fee * milestonePct / 100;
  const siteFee = (siteBasic || basicTotal) * siteWorkPct / 100 * feeRate;
  const feeDue = Math.round(stage >= 4 ? Math.max(milestoneFee, siteFee) : milestoneFee);
  return { pct: higherPct, feeDue, projectValue: Math.round(basicTotal * higherPct / 100), label: sched.label };
}

const PayNow = ({ invoice }) => (
  <a href={invoice.payment_link_url} target="_blank" rel="noopener noreferrer"
    className="ml-auto inline-flex items-center gap-2 px-4 py-2 rounded-full lg-btn-primary text-sm font-semibold transition-transform hover:-translate-y-px"
    data-testid="public-consultancy-pay-now">
    <CreditCard size={15} /> Pay {inr(invoice.total)} now
  </a>
);

const LockedPill = ({ schedule, stage, money, invoice }) => {
  const { pct, feeDue, label } = stageBilling(schedule, stage, money);
  return (
    <div className="flex flex-wrap items-center gap-3" data-testid="public-milestone-lock">
      <div className="text-[10px] uppercase tracking-wider text-emerald-800 font-bold">Current Billing Milestone</div>
      <div className="inline-flex items-center gap-2 bg-emerald-600 text-white rounded-lg px-3 py-2 shadow-sm">
        <span className="text-[10px] uppercase font-bold opacity-80">Stage</span>
        <span className="text-lg font-extrabold" data-testid="public-milestone-locked-stage">{stage}</span>
        <span className="text-sm font-semibold">· {label || `Stage ${stage}`}</span>
      </div>
      <div className="text-sm font-mono">
        <span className="text-stone-500">{pct}% of consultancy · </span>
        <span className="font-extrabold text-emerald-800" data-testid="public-milestone-locked-amount">{inr(feeDue)}</span>
        <span className="text-stone-500"> due this stage</span>
      </div>
      {invoice?.payment_link_url && <PayNow invoice={invoice} />}
    </div>
  );
};

const stepState = (stage, lockedStage, pay) => {
  if (pay.paid_stages.includes(stage)) return 'paid';
  if (pay.invoiced_stages.includes(stage)) return 'billed';
  if (stage === lockedStage) return 'current';
  if (stage < lockedStage) return 'reached';
  return 'upcoming';
};
const STEP_STYLE = {
  paid: ['bg-emerald-600 text-white border-emerald-600', 'Paid'],
  billed: ['bg-sky-600 text-white border-sky-600', 'Billed'],
  current: ['bg-white text-emerald-800 border-emerald-500 ring-4 ring-emerald-100', 'Current'],
  reached: ['bg-emerald-100 text-emerald-800 border-emerald-300', 'Reached'],
  upcoming: ['bg-white/60 text-stone-400 border-stone-300', 'Upcoming'],
};
const StageTimelineStrip = ({ schedule, lockedStage, pay }) => (
  <div className="mb-5" data-testid="stage-timeline-strip">
    <div className="relative flex items-start justify-between">
      <div className="absolute left-[10%] right-[10%] top-4 h-1 rounded-full bg-stone-200" />
      <div className="absolute left-[10%] top-4 h-1 rounded-full bg-emerald-500 transition-[width] duration-700"
        style={{ width: `${Math.max(0, Math.min(4, lockedStage - 1)) * 20}%` }} />
      {schedule.map((row) => {
        const st = stepState(Number(row.stage), lockedStage, pay);
        const [cls, tag] = STEP_STYLE[st];
        return (
          <div key={row.stage} className="relative z-10 flex flex-col items-center w-1/5 text-center px-1" data-testid={`stage-timeline-step-${row.stage}`} data-state={st}>
            <div className={`w-9 h-9 rounded-full border-2 flex items-center justify-center text-sm font-bold transition-colors ${cls}`}>
              {st === 'paid' ? <Check size={16} /> : row.stage}
            </div>
            <div className="mt-2 text-[11px] font-semibold text-stone-800 leading-tight">{row.label}</div>
            <div className={`mt-1 text-[9px] uppercase tracking-wider font-bold ${st === 'upcoming' ? 'text-stone-400' : 'text-emerald-700'}`}>{tag}</div>
          </div>
        );
      })}
    </div>
  </div>
);

const ScheduleTable = ({ schedule, lockedStage, money }) => (
  <div className="overflow-x-auto">
    <table className="w-full text-xs border border-stone-200">
      <thead className="bg-stone-50">
        <tr>
          <th className="text-left px-3 py-2 border-b border-stone-200 w-14">Stage</th>
          <th className="text-left px-3 py-2 border-b border-stone-200">Milestone</th>
          <th className="text-right px-3 py-2 border-b border-stone-200 w-20">% of Fee</th>
          <th className="text-right px-3 py-2 border-b border-stone-200 w-24">% of Site Work</th>
          <th className="text-right px-3 py-2 border-b border-stone-200 w-36 bg-emerald-50 text-emerald-900" data-testid="public-fee-due-header">Consultancy Fee Due</th>
          <th className="text-right px-3 py-2 border-b border-stone-200 w-40" data-testid="public-project-value-header">Cumulative Project Value Billed</th>
          <th className="text-left px-3 py-2 border-b border-stone-200">Notes</th>
        </tr>
      </thead>
      <tbody>
        {schedule.map((row) => {
          const { pct, feeDue, projectValue } = stageBilling(schedule, Number(row.stage), money);
          const isLocked = Number(row.stage) === lockedStage;
          return (
            <tr key={row.stage} className={`border-b border-stone-100 last:border-b-0 ${isLocked ? 'bg-emerald-50/60' : ''}`}>
              <td className={`px-3 py-2 font-bold ${isLocked ? 'text-emerald-800' : 'text-stone-800'}`}>{row.stage}{isLocked && ' ●'}</td>
              <td className="px-3 py-2 font-semibold text-stone-900">{row.label}</td>
              <td className="px-3 py-2 text-right font-mono text-stone-700">{row.pct_of_fee}%</td>
              <td className="px-3 py-2 text-right font-mono text-stone-700">{row.pct_of_site_work ? `${row.pct_of_site_work}%` : '\u2014'}</td>
              <td className={`px-3 py-2 text-right font-mono font-bold bg-emerald-50/40 ${isLocked ? 'text-emerald-900' : 'text-stone-700'}`} data-testid={`public-fee-due-stage-${row.stage}`}>{inr(feeDue)}</td>
              <td className={`px-3 py-2 text-right font-mono ${isLocked ? 'text-emerald-900 font-bold' : 'text-stone-500'}`} data-testid={`public-billable-stage-${row.stage}`}>
                {inr(projectValue)} <span className="text-[9px]">({pct}%)</span>
              </td>
              <td className="px-3 py-2 text-stone-600 text-[11px]">{row.note}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  </div>
);

const usePayInfo = (publicToken) => {
  const [pay, setPay] = useState({ open_invoice: null, paid_stages: [], invoiced_stages: [] });
  useEffect(() => {
    if (!publicToken) return;
    axios.get(`${API}/public/boq/${publicToken}/consultancy-pay`).then((r) => setPay(r.data)).catch(() => {});
  }, [publicToken]);
  return pay;
};

export default function ConsultancyBillingSchedule({ schedule, stage, fee, basicTotal, siteBasic, publicToken, className = '' }) {
  const pay = usePayInfo(publicToken);
  if (!Array.isArray(schedule) || !schedule.length) return null;
  const lockedStage = Number(stage) || 0;
  const money = { fee: Number(fee) || 0, basicTotal: Number(basicTotal) || 0, siteBasic: Number(siteBasic) || 0 };
  return (
    <div className={`rounded-xl border border-stone-200 bg-white p-5 shadow-sm ${className}`} data-testid="consultancy-billing-legend">
      <h3 className="text-lg font-bold text-stone-900 mb-1">Consultancy Billing Schedule</h3>
      <p className="text-xs text-stone-500 mb-3">
        How the design consultancy fee is invoiced across project stages. Stages 4 &amp; 5 are billed as the <em>higher</em> of the milestone % or the actual on-site work % completed.
      </p>
      <StageTimelineStrip schedule={schedule} lockedStage={lockedStage} pay={pay} />
      <div className="mb-4 rounded-lg border-2 border-emerald-200 bg-emerald-50/50 p-3">
        {lockedStage > 0 ? <LockedPill schedule={schedule} stage={lockedStage} money={money} invoice={pay.open_invoice} /> : (
          <div className="text-[11px] text-stone-500 italic" data-testid="public-milestone-not-set">
            Milestone stage will appear here once the studio locks the current stage from Edit BOQ.
          </div>
        )}
      </div>
      <ScheduleTable schedule={schedule} lockedStage={lockedStage} money={money} />
    </div>
  );
}
